// QRScanner - Advanced QR/Barcode scanner with intelligent actions
// Uses html5-qrcode for reliable scanning
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Html5Qrcode } from 'html5-qrcode';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { expensesAPI } from '../services/api';
import { 
  QrCode, Camera, User, FileText, Package, History,
  Plus, CheckCircle, X, Loader2, Receipt, Phone, Mail,
  Building, MapPin, Globe, ArrowRight, Scan
} from 'lucide-react';

const QRScanner = ({ isOpen, onClose, onScanComplete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Load scan history
  useEffect(() => {
    const saved = localStorage.getItem('optitrajet_scan_history');
    if (saved) {
      setScanHistory(JSON.parse(saved).slice(0, 20));
    }
  }, []);

  // Initialize scanner when dialog opens
  useEffect(() => {
    if (isOpen && !scanning) {
      startScanner();
    }
    return () => {
      stopScanner();
    };
  }, [isOpen]);

  const startScanner = async () => {
    try {
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop();
      }
      
      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        onScanSuccess,
        onScanFailure
      );
      
      setScanning(true);
    } catch (err) {
      console.error("Scanner error:", err);
      toast({
        title: "Erreur caméra",
        description: "Impossible d'accéder à la caméra. Vérifiez les permissions.",
        variant: "destructive"
      });
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && scanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.log("Scanner stop error:", err);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = (decodedText, decodedResult) => {
    // Vibrate if supported
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }
    
    processScannedData(decodedText);
  };

  const onScanFailure = (error) => {
    // Ignore continuous scan failures (normal behavior)
  };

  const processScannedData = (data) => {
    setProcessing(true);
    
    const result = detectDataType(data);
    setScanResult(result);
    
    // Save to history
    const historyEntry = {
      ...result,
      timestamp: new Date().toISOString(),
      rawData: data.substring(0, 200)
    };
    
    const newHistory = [historyEntry, ...scanHistory].slice(0, 20);
    setScanHistory(newHistory);
    localStorage.setItem('optitrajet_scan_history', JSON.stringify(newHistory));
    
    setProcessing(false);
    setActionModal(true);
    
    // Pause scanner
    stopScanner();
  };

  // Detect type of scanned data and suggest action
  const detectDataType = (data) => {
    const trimmed = data.trim();
    
    // VCard format (contact card)
    if (trimmed.toUpperCase().startsWith('BEGIN:VCARD') || trimmed.includes('MECARD:')) {
      const contact = parseVCard(trimmed);
      return {
        type: 'contact',
        icon: User,
        color: 'blue',
        title: t('qrScanner.contact'),
        description: contact.name || t('qrScanner.contact'),
        data: contact,
        suggestedAction: 'create_contact',
        actionLabel: t('qrScanner.createContact')
      };
    }
    
    // URL (could be invoice, receipt link)
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      if (trimmed.includes('invoice') || trimmed.includes('facture') || trimmed.includes('receipt')) {
        return {
          type: 'invoice_url',
          icon: FileText,
          color: 'green',
          title: t('qrScanner.invoice'),
          description: trimmed.substring(0, 50) + '...',
          data: { url: trimmed },
          suggestedAction: 'open_invoice',
          actionLabel: t('qrScanner.openLink')
        };
      }
      return {
        type: 'url',
        icon: Globe,
        color: 'purple',
        title: t('qrScanner.openLink'),
        description: trimmed.substring(0, 50) + '...',
        data: { url: trimmed },
        suggestedAction: 'open_url',
        actionLabel: t('qrScanner.openLink')
      };
    }
    
    // Email address
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return {
        type: 'email',
        icon: Mail,
        color: 'amber',
        title: 'Email',
        description: trimmed,
        data: { email: trimmed },
        suggestedAction: 'create_contact',
        actionLabel: t('qrScanner.createContact')
      };
    }
    
    // Phone number
    if (/^[\d\s\+\-\(\)\.]{8,20}$/.test(trimmed.replace(/\s/g, ''))) {
      return {
        type: 'phone',
        icon: Phone,
        color: 'indigo',
        title: t('invoiceSender.phoneNumber'),
        description: trimmed,
        data: { phone: trimmed },
        suggestedAction: 'create_contact',
        actionLabel: t('qrScanner.createContact')
      };
    }
    
    // JSON (structured data - could be expense/invoice)
    try {
      const json = JSON.parse(trimmed);
      if (json.amount || json.total || json.montant) {
        return {
          type: 'expense_data',
          icon: Receipt,
          color: 'red',
          title: t('qrScanner.receipt'),
          description: `${t('expenses.amount')}: ${json.amount || json.total || json.montant}`,
          data: json,
          suggestedAction: 'create_expense',
          actionLabel: t('qrScanner.createExpense')
        };
      }
      if (json.name || json.company || json.entreprise) {
        return {
          type: 'contact_data',
          icon: Building,
          color: 'blue',
          title: t('qrScanner.contact'),
          description: json.name || json.company || json.entreprise,
          data: json,
          suggestedAction: 'create_contact',
          actionLabel: t('qrScanner.createContact')
        };
      }
      return {
        type: 'json',
        icon: FileText,
        color: 'gray',
        title: 'JSON',
        description: 'JSON data',
        data: json,
        suggestedAction: 'review',
        actionLabel: t('common.close')
      };
    } catch {}
    
    // EAN/UPC barcode (numeric, 8-14 digits)
    if (/^\d{8,14}$/.test(trimmed)) {
      return {
        type: 'barcode',
        icon: Package,
        color: 'purple',
        title: t('qrScanner.barcode'),
        description: `Code: ${trimmed}`,
        data: { barcode: trimmed },
        suggestedAction: 'lookup_product',
        actionLabel: t('qrScanner.barcode')
      };
    }
    
    // Receipt/ticket text (contains price patterns)
    if (/\d+[,\.]\d{2}\s*(€|EUR|USD|\$|MAD|DH)/i.test(trimmed)) {
      const amountMatch = trimmed.match(/(\d+[,\.]\d{2})\s*(€|EUR|USD|\$|MAD|DH)/i);
      const amount = amountMatch ? parseFloat(amountMatch[1].replace(',', '.')) : null;
      
      return {
        type: 'receipt',
        icon: Receipt,
        color: 'red',
        title: t('qrScanner.receipt'),
        description: amount ? `${t('expenses.amount')}: ${amount}€` : t('qrScanner.receipt'),
        data: { 
          rawText: trimmed, 
          detectedAmount: amount,
          type: 'other'
        },
        suggestedAction: 'create_expense',
        actionLabel: t('qrScanner.createExpense')
      };
    }
    
    // Default: unknown text
    return {
      type: 'text',
      icon: FileText,
      color: 'gray',
      title: 'Text',
      description: trimmed.substring(0, 100),
      data: { text: trimmed },
      suggestedAction: 'review',
      actionLabel: t('common.close')
    };
  };

  // Parse VCard format
  const parseVCard = (data) => {
    const contact = {
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      address: '',
      website: ''
    };
    
    const lines = data.split(/[\r\n]+/);
    
    lines.forEach(line => {
      const upperLine = line.toUpperCase();
      
      if (upperLine.startsWith('FN:')) {
        contact.name = line.substring(3).trim();
      } else if (upperLine.startsWith('N:')) {
        const parts = line.substring(2).split(';');
        contact.lastName = parts[0]?.trim() || '';
        contact.firstName = parts[1]?.trim() || '';
        if (!contact.name) {
          contact.name = `${contact.firstName} ${contact.lastName}`.trim();
        }
      } else if (upperLine.startsWith('EMAIL')) {
        contact.email = line.split(':').pop()?.trim() || '';
      } else if (upperLine.startsWith('TEL')) {
        contact.phone = line.split(':').pop()?.trim() || '';
      } else if (upperLine.startsWith('ORG:')) {
        contact.company = line.substring(4).trim();
      } else if (upperLine.startsWith('ADR')) {
        const adrParts = line.split(':').pop()?.split(';') || [];
        contact.address = adrParts.filter(p => p.trim()).join(', ');
      } else if (upperLine.startsWith('URL:')) {
        contact.website = line.substring(4).trim();
      }
    });
    
    // MECARD format
    if (data.includes('MECARD:')) {
      const mecardMatch = data.match(/MECARD:(.+)/);
      if (mecardMatch) {
        const fields = mecardMatch[1].split(';');
        fields.forEach(field => {
          const [key, value] = field.split(':');
          if (key === 'N') contact.name = value?.replace(/,/g, ' ') || '';
          if (key === 'TEL') contact.phone = value || '';
          if (key === 'EMAIL') contact.email = value || '';
          if (key === 'ORG') contact.company = value || '';
          if (key === 'ADR') contact.address = value?.replace(/,/g, ', ') || '';
        });
      }
    }
    
    return contact;
  };

  // Execute the suggested action
  const executeAction = async () => {
    if (!scanResult) return;
    
    setProcessing(true);
    
    try {
      switch (scanResult.suggestedAction) {
        case 'create_contact':
          await createContact(scanResult.data);
          break;
        case 'create_expense':
          await createExpense(scanResult.data);
          break;
        case 'open_url':
        case 'open_invoice':
          window.open(scanResult.data.url, '_blank');
          toast({ title: t('qrScanner.openLink'), description: t('common.success') });
          break;
        case 'lookup_product':
          toast({ title: t('qrScanner.barcode'), description: `Code: ${scanResult.data.barcode}` });
          break;
        case 'review':
          if (scanResult.data.text) {
            navigator.clipboard?.writeText(scanResult.data.text);
            toast({ title: t('invoiceSender.linkCopied'), description: '' });
          }
          break;
      }
      
      setActionModal(false);
      setScanResult(null);
      
      if (onScanComplete) {
        onScanComplete(scanResult);
      }
      
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.message || t('common.error'),
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Create a contact/client from scanned data
  const createContact = async (data) => {
    const clientData = {
      name: data.name || data.company || `Contact ${new Date().toLocaleDateString()}`,
      email: data.email || '',
      phone: data.phone || '',
      company: data.company || '',
      address: data.address || '',
      notes: `${t('qrScanner.scanSuccess')} ${new Date().toLocaleDateString()}`
    };
    
    // Save to localStorage
    const savedClients = JSON.parse(localStorage.getItem('optitrajet_scanned_clients') || '[]');
    savedClients.push({ ...clientData, id: Date.now(), created_at: new Date().toISOString() });
    localStorage.setItem('optitrajet_scanned_clients', JSON.stringify(savedClients));
    
    toast({
      title: t('qrScanner.createContact'),
      description: clientData.name
    });
    navigate('/invoices');
  };

  // Create an expense from scanned data
  const createExpense = async (data) => {
    const expenseData = {
      description: data.description || t('qrScanner.receipt'),
      amount: data.detectedAmount || data.amount || data.total || 0,
      type: data.type || 'other',
      date: new Date().toISOString(),
      notes: `${t('qrScanner.scanSuccess')} - ${JSON.stringify(data).substring(0, 200)}`
    };
    
    try {
      await expensesAPI.create(expenseData);
      toast({
        title: t('qrScanner.createExpense'),
        description: `${t('expenses.amount')}: ${expenseData.amount}€`
      });
      navigate('/expenses');
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.error'),
        variant: 'destructive'
      });
    }
  };

  // Handle manual input
  const handleManualSubmit = () => {
    if (manualInput.trim()) {
      processScannedData(manualInput.trim());
      setManualInput('');
    }
  };

  // Continue scanning
  const continueScan = () => {
    setScanResult(null);
    setActionModal(false);
    startScanner();
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600 border-blue-200',
      green: 'bg-green-100 text-green-600 border-green-200',
      red: 'bg-red-100 text-red-600 border-red-200',
      amber: 'bg-amber-100 text-amber-600 border-amber-200',
      purple: 'bg-purple-100 text-purple-600 border-purple-200',
      indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
      gray: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return colors[color] || colors.gray;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-indigo-500" />
              Scanner QR / Code-barres
            </DialogTitle>
            <DialogDescription>
              Scannez un QR code, code-barres ou carte de visite
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Scanner View */}
            <div className="relative aspect-square bg-black rounded-xl overflow-hidden">
              <div id="qr-reader" className="w-full h-full" />
              
              {!scanning && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-center text-white">
                    <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm opacity-70">Initialisation caméra...</p>
                  </div>
                </div>
              )}
              
              {processing && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
            </div>

            {/* Manual Input */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-500">Ou entrez le code manuellement</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Collez le contenu du QR code..."
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
                />
                <Button onClick={handleManualSubmit} disabled={!manualInput.trim()}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Scan History Toggle */}
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowHistory(!showHistory)}
            >
              <History className="w-4 h-4 mr-2" />
              Historique ({scanHistory.length})
            </Button>

            {/* Scan History */}
            <AnimatePresence>
              {showHistory && scanHistory.length > 0 && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="space-y-2 overflow-hidden max-h-48 overflow-y-auto"
                >
                  {scanHistory.slice(0, 5).map((item, idx) => {
                    const IconComponent = item.icon || FileText;
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100"
                        onClick={() => processScannedData(item.rawData)}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getColorClasses(item.color)}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.title}</p>
                          <p className="text-xs text-gray-500 truncate">{item.description}</p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(item.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Quick Actions Legend */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <User className="w-3 h-3 text-blue-500" />
                <span>Carte visite → Contact</span>
              </div>
              <div className="flex items-center gap-2">
                <Receipt className="w-3 h-3 text-red-500" />
                <span>Reçu → Dépense</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-3 h-3 text-green-500" />
                <span>Facture → Rattacher</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="w-3 h-3 text-purple-500" />
                <span>Code-barres → Produit</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Modal - What to do with scanned data */}
      <Dialog open={actionModal} onOpenChange={setActionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Scan réussi !
            </DialogTitle>
          </DialogHeader>

          {scanResult && (
            <div className="space-y-4">
              {/* Result Card */}
              <Card className={`border-2 ${getColorClasses(scanResult.color)}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getColorClasses(scanResult.color)}`}>
                      <scanResult.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <Badge variant="outline" className="mb-2">{scanResult.title}</Badge>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {scanResult.description}
                      </p>
                      
                      {/* Show parsed contact info */}
                      {scanResult.type === 'contact' && scanResult.data && (
                        <div className="mt-3 space-y-1 text-sm">
                          {scanResult.data.email && (
                            <p className="flex items-center gap-2">
                              <Mail className="w-3 h-3" /> {scanResult.data.email}
                            </p>
                          )}
                          {scanResult.data.phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="w-3 h-3" /> {scanResult.data.phone}
                            </p>
                          )}
                          {scanResult.data.company && (
                            <p className="flex items-center gap-2">
                              <Building className="w-3 h-3" /> {scanResult.data.company}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Show detected amount */}
                      {(scanResult.type === 'receipt' || scanResult.type === 'expense_data') && scanResult.data?.detectedAmount && (
                        <div className="mt-3 p-2 bg-green-50 rounded-lg">
                          <p className="text-lg font-bold text-green-600">
                            {scanResult.data.detectedAmount.toFixed(2)} €
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={continueScan}
                >
                  Scanner autre
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"
                  onClick={executeAction}
                  disabled={processing}
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  {scanResult.actionLabel}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QRScanner;
